type CustomerInfoType = {
	customerId: string;
};

type ShipmentType = {
	adjustedMerchandizeTotalTax: number;
	adjustedShippingTotalTax: number;
	gift: boolean;
	merchandizeTotalTax: number;
	productSubTotal: number;
	productTotal: number;
	shipmentId: string;
	shipmentTotal: number;
	shippingStatus: string;
	shippingTotal: number;
	shippingTotalTax: number;
	taxTotal: number;
};

type ShippingItemType = {
	adjustedTax: number;
	basePrice: number;
	itemId: string;
	itemText: string;
	price: number;
	priceAfterItemDiscount: number;
	shipmentId: string;
	tax: number;
	taxBasis: number;
	taxClassId: string;
	taxRate: number;
};

type Discount = {
	amount?: number;
	percentage?: number;
	priceBookId?: string;
	type: string;
};

type ProductDetailsLink = {
	productDescription?: string;
	productId: string;
	productName?: string;
	title?: string;
};

type ProductListLink = {
	description?: string;
	name?: string;
	public?: boolean;
	title?: string;
	type?: string;
};

export type PriceAdjustment = {
	appliedDiscount?: Discount;
	couponCode?: string;
	createdBy?: string;
	creationDate?: any;
	custom?: boolean;
	itemText?: string;
	lastModified?: any;
	manual?: boolean;
	price?: number;
	priceAdjustmentId?: string;
	promotionId?: string;
	promotionLink?: string;
	reasonCode?: string;
};

type OptionItem = {
	adjustedTax?: number;
	basePrice?: number;
	bonusDiscountLineItemId?: string;
	bonusProductLineItem?: boolean;
	bundledProductItems?: Array<ProductItem>;
	gift?: boolean;
	giftMessage?: string;
	inventoryId?: string;
	itemId?: string;
	itemText?: string;
	optionId: string;
	optionItems?: Array<OptionItem>;
	optionValueId: string;
	price?: number;
	priceAdjustments?: Array<PriceAdjustment>;
	priceAfterItemDiscount?: number;
	priceAfterOrderDiscount?: number;
	productId?: string;
	productListItem?: ProductListItemReference;
	productName?: string;
	quantity?: number;
	shipmentId?: string;
	shippingItemId?: string;
	tax?: number;
	taxBasis?: number;
	taxClassId?: string;
	taxRate?: number;
};

type ProductListItemReference = {
	id: string;
	priority?: number;
	productDetailsLink?: ProductDetailsLink;
	productList?: ProductListLink;
	public?: boolean;
	purchasedQuantity?: number;
	quantity?: number;
	type?: string;
};

type PaymentCardSpec = {
	cardType?: string;
	checksumVerificationEnabled?: boolean;
	description?: string;
	image?: string;
	name?: string;
	numberLengths?: Array<string>;
	numberPrefixes?: Array<string>;
} & {
	[key: string]: any;
};

export type ExpressButtonStyleConfig = {
	buttonColor?: 'default' | 'primary' | 'secondary';
	buttonType?: 'plain' | 'outlined' | 'contained';
	buttonRadius?: '0' | '4' | '6' | '8';
};

type ExpressButton = {
	buttonStyleConfig?: ExpressButtonStyleConfig;
};

export type ExpressButtonsConfig = {
	pdp?: ExpressButton;
	plp?: ExpressButton;
	checkout?: ExpressButton;
	minicart?: ExpressButton;
};

export enum CategoriesStatus {
	ALLOWED = 'allowedCategories',
	EXCLUDED = 'excludedCategories',
}

export interface CategoriesConfig {
	[CategoriesStatus.ALLOWED]?: string[];
	[CategoriesStatus.EXCLUDED]?: string[];
}

export enum PaymentMessageConfigurationSection {
	PDP = 'pdp',
}

export interface MessageConfigurationsConfig {
	sections: {
		[key in PaymentMessageConfigurationSection]?: CategoriesConfig;
	};
}

export type PaymentMethod = {
	cards?: Array<PaymentCardSpec>;
	description?: string;
	id: string;
	image?: string;
	name?: string;
	paymentProcessorId?: string;
	c_applicableForBasket: boolean;
	c_maxCountryPaymentAmount?: string;
	c_publicConfigurations?: Object;
	c_expressButtons?: ExpressButtonsConfig;
	c_messageConfigurations?: MessageConfigurationsConfig;
	c_contentAsset?: string;
	c_excludedProducts?: string[];
} & {
	[key: string]: any;
};

type TOrderPrice = {
	appliedDiscount?: {
		percentage?: number;
	};
	price?: number;
	promotionId?: string;
	couponCode?: string;
};

export type BasketState = {
	adjustedMerchandizeTotalTax: number;
	adjustedShippingTotalTax: number;
	applicablePaymentMethods: Array<PaymentMethod>;
	agentBasket: boolean;
	basketId: string;
	channelType: string;
	creationDate: string;
	currency: string;
	customerInfo: CustomerInfoType;
	lastModified: string;
	merchandizeTotalTax: number;
	productItems?: Array<ProductItem>;
	giftCertificateItems?: Array<GiftCertificateItem>;
	notes: Object;
	orderTotal: number;
	productSubTotal: number;
	productTotal: number;
	shipments: ShipmentType[];
	shippingItems: ShippingItemType[];
	shippingTotal: number;
	shippingTotalTax: number;
	subTotal: number;
	taxation: string;
	taxTotal: number;
	orderPriceAdjustments?: Array<TOrderPrice>;
	initializationId: string;
	productItemsIncludingHidden?: Array<ProductItem>;
};

export enum EGiftCertificateType {
	VIRTUAL = 'VIRTUAL',
	PHYSICAL = 'PHYSICAL',
}

export type GiftCertificateItem = {
	amount: number;
	giftCertificateItemId?: string;
	message?: string;
	recipientEmail: string;
	recipientName?: string;
	senderName?: string;
	shipmentId?: string;
	c_delayDispatchDate?: string;
	c_giftCertificateType?: EGiftCertificateType;
	c_senderEmail?: string;
};

export type ProductItem = {
	adjustedTax?: number;
	basePrice?: number;
	bonusDiscountLineItemId?: string;
	bonusProductLineItem?: boolean;
	bundledProductItems?: Array<ProductItem>;
	gift?: boolean;
	giftMessage?: string;
	inventoryId?: string;
	itemId?: string;
	itemText?: string;
	optionItems?: Array<OptionItem>;
	price?: number;
	priceAdjustments?: Array<PriceAdjustment>;
	priceAfterItemDiscount?: number;
	priceAfterOrderDiscount?: number;
	productId?: string;
	productListItem?: ProductListItemReference;
	productName?: string;
	quantity?: number;
	shipmentId?: string;
	shippingItemId?: string;
	tax?: number;
	taxBasis?: number;
	taxClassId?: string;
	taxRate?: number;
	c_variationGroupId?: string;
	c_isPartOfBundleKit?: boolean;
	c_bundleKitId?: string;
	c_isParentBundleKit?: boolean;
	c_isPersonalized?: boolean;
	c_personalizationMessage?: string;
	c_personalizationColor?: string;
	c_positionPersonalizationMessage?: string;
	c_personalizationFont?: string;
	c_personalizationNumber?: string;
	c_positionPersonalizationNumber?: string;
	c_isMonogramServiceItem?: boolean;
	c_monogramParentItemId?: string;
	getParentBundleKitId?: () => string;
};

export enum ExpressPaymentButtonSection {
	PDP = 'pdp',
	PLP = 'plp',
	MINICART = 'minicart',
	CHECKOUT = 'checkout',
	GALLERY = 'gallery',
}
